import { useEffect, useReducer, useRef, useState } from 'react';
import { useLoaderData, useLocation, useNavigate, useRevalidator } from 'react-router';
import type { LoaderFunctionArgs } from 'react-router';

import { Badge, Button, Card, Divider, Icon } from '@ds/core';
import { Alert, EmptyState } from '@ds/feedback';
import { PageHeader } from '@/comuns/PageHeader';
import { enviarFila, semRede } from '@/comuns/filaOffline';
import { dataEHoraPorExtenso, horaDe, idadeEm } from '@/comuns/formato';
import { useAviso } from '@/comuns/toast';
import { diaEmSaoPaulo, duracaoEscrita, estaPausado, segundosDeConsulta } from '@/data/atendimentoEmCurso';
import type { PausaDoAtendimento } from '@/data/atendimentoEmCurso';
import { dados } from '@/data/porta';
import { valorEmVigor } from '@/data/preferencias';
import type { AtendimentoEmCurso, FichaPreenchida } from '@/data/tipos';
import { pode } from '@/session/sessao';
import type { NotaDeIa } from '@/data/notaDeIa';
import type { PedidoDeAnotar } from './atendimento/AnotarComVecchiaModal';
import { NotaDaIaCard } from './atendimento/NotaDaIaCard';
import { ReabrirAtendimentoModal } from './atendimento/ReabrirAtendimentoModal';
import { RevisarNotaModal } from './atendimento/RevisarNotaModal';
import { useNotaDaIa } from './atendimento/useNotaDaIa';
import { imprimirConsulta } from './atendimento/imprimirConsulta';
import { FichaLida, PreencherFichaModal } from './paciente/FichaClinica';
import { AlterarRespostasModal } from './paciente/AlterarRespostasModal';
import { ImpressaoDaFichaModal } from './paciente/ImpressaoDaFichaModal';

export interface CargaDoEditorDeAtendimento {
    consulta: AtendimentoEmCurso;
    pausas: PausaDoAtendimento[];
    /** A preferência "Permitir criar atendimentos rascunhos": ligada, a ficha se guarda sozinha. */
    rascunhosLigados: boolean;
    /** As notas da IA desta consulta que a pessoa vê. Sem permissão de atendimento, nenhuma. */
    notasDaIa: NotaDeIa[];
}

export async function carregarAtendimentoEditor({ params }: LoaderFunctionArgs): Promise<CargaDoEditorDeAtendimento> {
    const consulta = await dados().atendimentoEmCurso(params.atendimentoId ?? '');
    const [pausas, preferencias, notasDaIa] = await Promise.all([
        dados().pausasDoAtendimento(consulta.id),
        dados().preferencias(),
        pode('atendimentos') ? dados().notasDeIaDaConsulta(consulta.id) : Promise.resolve([]),
    ]);
    return { consulta, pausas, rascunhosLigados: valorEmVigor('at.rascunhos', preferencias) === true, notasDaIa };
}

export function AtendimentoEditor() {
    const { consulta, pausas, rascunhosLigados, notasDaIa } = useLoaderData() as CargaDoEditorDeAtendimento;
    const navigate = useNavigate();
    const revalidator = useRevalidator();
    const aviso = useAviso();
    const [fichaAberta, setFichaAberta] = useState(false);
    const [reabrindo, setReabrindo] = useState(false);
    const [fichaImpressa, setFichaImpressa] = useState<FichaPreenchida | null>(null);
    const [fichaEmCorrecao, setFichaEmCorrecao] = useState<FichaPreenchida | null>(null);
    const [ocupado, setOcupado] = useState(false);
    const [notaEmRevisao, setNotaEmRevisao] = useState<string | null>(null);
    const [offline, setOffline] = useState(() => semRede());
    const [, baterRelogio] = useReducer((n: number) => n + 1, 0);
    const fichasRef = useRef<HTMLDivElement>(null);
    const avisoRef = useRef(aviso);
    useEffect(() => { avisoRef.current = aviso; });

    const emCurso = consulta.situacao === 'em-atendimento' && Boolean(consulta.iniciadoEm) && !consulta.encerradoEm;
    const pausado = emCurso && estaPausado(pausas);
    const notaDaIa = useNotaDaIa({ atendimentoId: consulta.id, notasIniciais: notasDaIa, consultaPausada: pausado, consultaAberta: emCurso });

    const notaParaRevisar = notaDaIa.notas.find((n) => n.id === notaEmRevisao && n.situacao === 'revisao');

    /* Veio do "Anotar com VecchIA": o consentimento já foi dado no modal, e a
       escuta começa aqui. O pedido sai do histórico logo, para recarregar a
       página não abrir uma segunda gravação. */
    const location = useLocation();
    const pedidoDeAnotar = (location.state as { anotarComVecchia?: PedidoDeAnotar } | null)?.anotarComVecchia;
    const anotarJaPedido = useRef(false);
    const { comecar: comecarNota, aberta: notaAberta } = notaDaIa;
    useEffect(() => {
        if (!pedidoDeAnotar || anotarJaPedido.current) return;
        anotarJaPedido.current = true;
        /* Direto no histórico, sem `navigate`: navegar recarregaria a consulta e
           a lista de notas chegaria sem a gravação que acabou de começar. */
        const historico = window.history.state as Record<string, unknown> | null;
        window.history.replaceState({ ...historico, usr: null }, '');
        if (!emCurso || notaAberta || !pode('atendimentos')) return;
        comecarNota('consulta', pedidoDeAnotar).catch((e: unknown) => {
            avisoRef.current({
                title: 'A VecchIA não começou a escutar',
                description: e instanceof Error ? e.message : 'Use o botão "Gravar consulta" no cartão Nota da IA.',
                tone: 'danger',
            });
        });
    }, [pedidoDeAnotar, emCurso, notaAberta, comecarNota]);

    /* O cronômetro só bate enquanto a consulta corre: um intervalo numa consulta
       encerrada ou pausada é trabalho à toa e bateria do tablet. */
    useEffect(() => {
        if (!emCurso || pausado) return;
        const t = window.setInterval(baterRelogio, 30_000);
        return () => window.clearInterval(t);
    }, [emCurso, pausado]);

    /* Sem rede, o rascunho da ficha vai para a fila do navegador; quando a rede
       volta — ou quando a página abre de novo com rede —, a fila sai. */
    useEffect(() => {
        const enviarGuardados = () => {
            void enviarFila(async (item) => {
                await dados().salvarRascunhoDaFicha(item.atendimentoId, item.modeloId, item.respostas);
            }).then(({ enviados, falhas }) => {
                if (enviados > 0) {
                    avisoRef.current({
                        title: 'Rascunho enviado',
                        description: enviados === 1 ? 'A ficha escrita sem conexão foi guardada.' : `${enviados} fichas escritas sem conexão foram guardadas.`,
                    });
                }
                if (falhas.length > 0) avisoRef.current({ title: 'Parte do rascunho não foi enviada', description: falhas[0], tone: 'danger' });
            });
        };
        const semConexao = () => setOffline(true);
        const comConexao = () => { setOffline(false); enviarGuardados(); };
        if (!semRede()) enviarGuardados();
        window.addEventListener('offline', semConexao);
        window.addEventListener('online', comConexao);
        return () => {
            window.removeEventListener('offline', semConexao);
            window.removeEventListener('online', comConexao);
        };
    }, []);

    const instante = consulta.encerradoEm ? new Date(consulta.encerradoEm).getTime() : Date.now();
    const segundos = consulta.iniciadoEm ? segundosDeConsulta(consulta.iniciadoEm, pausas, instante) : null;
    const decorrido = segundos === null ? null : duracaoEscrita(segundos);
    const passouDoPrevisto = emCurso && segundos !== null && segundos / 60 > consulta.duracaoPrevistaMin;
    const encerradaHoje = consulta.situacao === 'concluido' && consulta.encerradoEm !== undefined
        && diaEmSaoPaulo(consulta.encerradoEm) === diaEmSaoPaulo(Date.now());

    const iniciar = async () => {
        setOcupado(true);
        try {
            const aberta = await dados().iniciarAtendimento(consulta.id);
            aviso({ title: 'Consulta aberta', description: consulta.paciente });
            /* O id muda quando o atendimento nasce agora: a URL precisa seguir,
               senão recarregar a página abriria a consulta de novo. */
            if (aberta.id !== consulta.id) navigate(`/app/atendimentos/editor/${aberta.id}`, { replace: true });
            else revalidator.revalidate();
        } catch (e) {
            aviso({ title: 'Não foi possível abrir', description: e instanceof Error ? e.message : undefined, tone: 'danger' });
        } finally {
            setOcupado(false);
        }
    };

    const encerrar = async () => {
        setOcupado(true);
        try {
            await dados().encerrarAtendimento(consulta.id);
            aviso({ title: 'Consulta encerrada', description: `${consulta.paciente} · ${decorrido ?? ''}` });
            revalidator.revalidate();
        } catch (e) {
            aviso({ title: 'Não foi possível encerrar', description: e instanceof Error ? e.message : undefined, tone: 'danger' });
        } finally {
            setOcupado(false);
        }
    };

    const alternarPausa = async () => {
        setOcupado(true);
        try {
            if (pausado) await dados().retomarAtendimento(consulta.id);
            else await dados().pausarAtendimento(consulta.id);
            aviso({ title: pausado ? 'Cronômetro retomado' : 'Cronômetro pausado', description: consulta.paciente });
            revalidator.revalidate();
        } catch (e) {
            aviso({ title: pausado ? 'Não foi possível retomar' : 'Não foi possível pausar', description: e instanceof Error ? e.message : undefined, tone: 'danger' });
        } finally {
            setOcupado(false);
        }
    };

    const imprimir = () => {
        const periodo = consulta.iniciadoEm
            ? `Início ${dataEHoraPorExtenso(consulta.iniciadoEm)}${consulta.encerradoEm ? ` · fim ${dataEHoraPorExtenso(consulta.encerradoEm)}` : ' · em andamento'}${decorrido ? ` · ${decorrido}` : ''}`
            : 'Consulta ainda não aberta';
        const abriu = imprimirConsulta(
            {
                paciente: consulta.paciente,
                linhas: [[consulta.procedimento || 'Consulta', consulta.profissional, consulta.sala].filter(Boolean).join(' · '), periodo],
                ...(consulta.alergias ? { alergias: consulta.alergias } : {}),
            },
            consulta.fichasDaConsulta.length > 0 ? fichasRef.current : null,
        );
        if (!abriu) aviso({ title: 'Permita pop-ups para imprimir', description: 'O navegador bloqueou a janela de impressão.', tone: 'warning' });
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <PageHeader
                title={consulta.paciente}
                description={
                    [
                        consulta.procedimento || 'Consulta',
                        consulta.profissional,
                        consulta.sala,
                        `previsto para ${horaDe(consulta.previstoPara)}`,
                        consulta.nascimento ? idadeEm(consulta.nascimento) : null,
                    ].filter(Boolean).join(' · ')
                }
                actions={
                    <>
                        <Button variant="secondary" onClick={() => navigate(`/app/contatos/${consulta.contatoId}`)}>Abrir ficha do paciente</Button>
                        {consulta.iniciadoEm && (
                            <Button variant="secondary" iconLeft={<Icon name="printer" size={15} />} onClick={imprimir}>Imprimir</Button>
                        )}
                        {/* Abrir a consulta é UMA ação: ela vive no corpo, junto da
                            explicação do que muda ao abrir. Repetir o botão no
                            cabeçalho só duplicaria o mesmo comando na tela. */}
                        {/* Preencher ficha também é uma ação só: com ficha na tela ela
                            fica no cabeçalho; sem nenhuma, fica no vazio que explica
                            o que ela faz. */}
                        {emCurso && consulta.fichasDaConsulta.length > 0 && (
                            <Button variant="secondary" iconLeft={<Icon name="clipboard-pen" size={15} />} onClick={() => setFichaAberta(true)}>Preencher ficha</Button>
                        )}
                        {encerradaHoje && (
                            <Button variant="secondary" iconLeft={<Icon name="rotate-ccw" size={15} />} onClick={() => setReabrindo(true)} disabled={offline}>
                                Reabrir atendimento
                            </Button>
                        )}
                        {emCurso && (
                            <Button iconLeft={<Icon name="square-check" size={15} />} onClick={() => void encerrar()} loading={ocupado} disabled={offline}>Encerrar</Button>
                        )}
                    </>
                }
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <Badge tone={consulta.situacao === 'concluido' ? 'success' : pausado ? 'warning' : emCurso ? 'info' : 'warning'} dot>
                        {consulta.situacao === 'concluido' ? 'Concluída' : pausado ? 'Pausada' : emCurso ? 'Em atendimento' : 'Aguardando'}
                    </Badge>
                    {emCurso && (
                        <Button
                            size="sm" variant="secondary" iconLeft={<Icon name={pausado ? 'play' : 'pause'} size={14} />}
                            onClick={() => void alternarPausa()} disabled={ocupado || offline}
                        >
                            {pausado ? 'Retomar cronômetro' : 'Pausar cronômetro'}
                        </Button>
                    )}
                    {decorrido && (
                        <span
                            /* `off` de propósito: um cronômetro que se anuncia a cada
                               atualização atropela o leitor de tela de quem está
                               digitando a evolução. */
                            aria-live="off"
                            title={`Previsto: ${consulta.duracaoPrevistaMin} min${pausas.length > 0 ? ` · descontadas ${pausas.length} ${pausas.length === 1 ? 'pausa' : 'pausas'}` : ''}`}
                            style={{
                                fontFamily: 'var(--font-ui)', fontSize: 13, fontWeight: 600, fontVariantNumeric: 'tabular-nums',
                                color: passouDoPrevisto ? 'var(--warning-strong)' : 'var(--text-secondary)',
                            }}
                        >
                            {consulta.encerradoEm ? 'durou' : pausado ? 'parada em' : 'aberta há'} {decorrido}
                            {passouDoPrevisto ? ` · passou dos ${consulta.duracaoPrevistaMin} min previstos` : ''}
                        </span>
                    )}
                </div>
            </PageHeader>

            {offline && (
                <Alert tone="warning" title="Sem conexão" dismissible={false}>
                    {rascunhosLigados && emCurso ? 'O que você escrever na ficha fica guardado neste navegador e é enviado quando a conexão voltar. ' : ''}
                    Pausar, encerrar, reabrir e salvar no prontuário esperam a rede.
                </Alert>
            )}

            {/* Alergia é o aviso que não pode depender de alguém abrir outra aba. */}
            {consulta.alergias && (
                <Alert tone="warning" title="Alergias registradas" dismissible={false}>{consulta.alergias}</Alert>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: 'var(--layout-colunas, minmax(0,1.5fr) minmax(0,1fr))', gap: 16, alignItems: 'start' }}>
                <div ref={fichasRef} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    {consulta.situacao === 'aguardando' ? (
                        <Card padding={0}>
                            <EmptyState
                                icon="play"
                                title="A consulta ainda não foi aberta"
                                description="Abrir a consulta marca o início — é dele que sai a duração e é ele que permite preencher ficha."
                                action={<Button onClick={() => void iniciar()} loading={ocupado}>Abrir consulta</Button>}
                            />
                        </Card>
                    ) : consulta.fichasDaConsulta.length === 0 ? (
                        <Card padding={0}>
                            <EmptyState
                                icon="clipboard-pen" title="Nenhuma ficha nesta consulta"
                                description="A ficha preenchida aqui fica amarrada a esta consulta, não ao paciente em geral."
                                action={emCurso ? <Button onClick={() => setFichaAberta(true)}>Preencher ficha</Button> : undefined}
                            />
                        </Card>
                    ) : (
                        consulta.fichasDaConsulta.map((f) => (
                            <FichaLida
                                key={f.id} ficha={f} modelo={consulta.modelos.find((m) => m.id === f.modeloId)}
                                onImprimir={() => setFichaImpressa(f)}
                                {...(pode('atendimentos') && f.definicaoUsada ? { onAlterar: () => setFichaEmCorrecao(f) } : {})}
                            />
                        ))
                    )}
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    {pode('atendimentos') && (
                        <NotaDaIaCard nota={notaDaIa} modelos={consulta.modelos} consultaAberta={emCurso} offline={offline} onRevisar={setNotaEmRevisao} />
                    )}
                    <Card title="Consulta" padding={18}>
                        <dl style={{ margin: 0, display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '9px 16px', fontSize: 13 }}>
                            {([
                                ['Previsto', `${horaDe(consulta.previstoPara)} · ${consulta.duracaoPrevistaMin} min`],
                                ['Início', consulta.iniciadoEm ? dataEHoraPorExtenso(consulta.iniciadoEm) : '—'],
                                ['Fim', consulta.encerradoEm ? dataEHoraPorExtenso(consulta.encerradoEm) : '—'],
                                ['Pausas', pausas.length === 0 ? '—' : String(pausas.length)],
                                ['Profissional', consulta.profissional || '—'],
                                ['Sala', consulta.sala ?? '—'],
                            ] as const).map(([rotulo, valor]) => (
                                <div key={rotulo} style={{ display: 'contents' }}>
                                    <dt style={{ color: 'var(--text-secondary)' }}>{rotulo}</dt>
                                    <dd style={{ margin: 0, textAlign: 'right', fontWeight: 600, color: 'var(--text-primary)' }}>{valor}</dd>
                                </div>
                            ))}
                        </dl>
                    </Card>

                    {/* Histórico ao lado, não em outra tela: a conduta de hoje se
                        escreve olhando a evolução da última sessão. */}
                    <Card title="Consultas anteriores" subtitle={`${consulta.historico.length} fichas no prontuário`} padding={18}>
                        {consulta.historico.length === 0 ? (
                            <p style={{ margin: 0, fontSize: 13, color: 'var(--text-tertiary)' }}>Primeira consulta com ficha deste paciente.</p>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                {consulta.historico.slice(0, 4).map((f) => (
                                    <div key={f.id}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                            <span style={{ flex: 1, fontFamily: 'var(--font-ui)', fontSize: 13, fontWeight: 600 }}>{f.nome}</span>
                                            <span style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>{dataEHoraPorExtenso(f.criadoEm)}</span>
                                        </div>
                                        <Divider spacing={8} />
                                    </div>
                                ))}
                                <Button variant="link" onClick={() => navigate(`/app/contatos/${consulta.contatoId}/prontuario`)}>Ver prontuário completo</Button>
                            </div>
                        )}
                    </Card>
                </div>
            </div>

            <PreencherFichaModal
                open={fichaAberta} contatoId={consulta.contatoId}
                prontuario={{
                    fichas: consulta.fichasDaConsulta,
                    modelos: consulta.modelos,
                    /* Uma consulta só na lista: a ficha aberta daqui pertence a
                       ESTA consulta, e oferecer as outras seria convite ao erro. */
                    atendimentos: [{ id: consulta.id, rotulo: `Esta consulta · ${horaDe(consulta.previstoPara)}` }],
                }}
                {...(rascunhosLigados && emCurso ? { rascunho: { atendimentoId: consulta.id } } : {})}
                onClose={() => setFichaAberta(false)} onSalva={() => revalidator.revalidate()}
            />

            {fichaImpressa && (
                <ImpressaoDaFichaModal
                    ficha={fichaImpressa} paciente={consulta.paciente} nascimento={consulta.nascimento}
                    onFechar={() => setFichaImpressa(null)}
                />
            )}
            {fichaEmCorrecao && (
                <AlterarRespostasModal
                    ficha={fichaEmCorrecao} contatoId={consulta.contatoId}
                    onFechar={() => setFichaEmCorrecao(null)} onAlterada={() => revalidator.revalidate()}
                />
            )}
            {reabrindo && (
                <ReabrirAtendimentoModal
                    atendimentoId={consulta.id} paciente={consulta.paciente}
                    onFechar={() => setReabrindo(false)} onReaberta={() => revalidator.revalidate()}
                />
            )}
            {notaParaRevisar && (
                <RevisarNotaModal
                    nota={notaParaRevisar}
                    onFechar={() => setNotaEmRevisao(null)}
                    onAprovada={() => {
                        void notaDaIa.recarregar();
                        revalidator.revalidate();
                    }}
                />
            )}
        </div>
    );
}
